import React, { useState } from "react";
import { Scale, Clock, MapPin, FileText, Users } from "lucide-react";

const DIARY = [
  { time: "10:30 AM", court: "Bombay High Court", room: "Court Room 14 · Item 28", title: "Malhotra vs. State of Maharashtra & Ors.", meta: "Writ Petition 4812/2023", stage: "For Admission" },
  { time: "12:00 PM", court: "City Civil Court, Dindoshi", room: "Court Room 04 · Item 12", title: "Reddy vs. Kohli Property Matter", meta: "Commercial Suit 319/2022", stage: "Cross-examination of PW-1" },
  { time: "02:15 PM", court: "NCLT Mumbai, Bench II", room: "Court Hall 2 · Item 06", title: "M/s Apex Logistics vs. Bharat Freight Ltd.", meta: "Company Petition 88/2024", stage: "Final Arguments" },
];

const CASE_FIELDS = [
  ["CNR Number", "MAHC01-004812-2023"],
  ["Presiding Judge", "Hon'ble Justice S. C. Gupte"],
  ["Opposing Counsel", "Adv. K. R. Chidambaram"],
  ["Filed On", "14 March 2023"],
  ["Next Hearing", "Tomorrow · 10:30 AM"],
  ["Stage", "For Admission"],
];

const TABS = [
  { id: "diary", label: "Daily court diary" },
  { id: "case", label: "Case record" },
  { id: "portal", label: "Client portal" },
];

const Chrome = ({ children }) => (
  <div className="overflow-hidden rounded-xl border border-border bg-white shadow-2xl shadow-navy/10">
    <div className="flex items-center gap-3 border-b border-navy-light bg-navy px-4 py-3">
      <span className="flex gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]" />
      </span>
      <span className="flex items-center gap-2 text-[11px] text-slate-300">
        <Scale className="h-3.5 w-3.5 text-gold" />
        Advocate Diary — Sharma &amp; Associates
      </span>
    </div>
    {children}
  </div>
);

export const ProductPreview = () => {
  const [tab, setTab] = useState("diary");

  return (
    <section
      id="preview"
      data-testid="landing-preview"
      className="border-b border-border bg-gradient-to-b from-[#FAFBFD] via-[#F4F6F9] to-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8"
    >
      <div className="mx-auto max-w-6xl">
        <p className="font-sans text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">
          Inside the product
        </p>
        <h2 className="mt-3 font-display text-3xl font-semibold tracking-tight text-navy sm:text-4xl">
          Your chamber, on one screen
        </h2>

        <div className="mt-8 flex flex-wrap gap-2">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`preview-tab-${t.id}`}
              className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
                tab === t.id
                  ? "bg-navy text-white"
                  : "border border-border bg-white text-navy/70 hover:text-navy"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          <Chrome>
            {tab === "diary" && (
              <div data-testid="preview-panel-diary" className="divide-y divide-border">
                <div className="flex items-baseline justify-between px-5 py-4">
                  <div>
                    <p className="font-display text-xl text-navy">Tomorrow&apos;s board</p>
                    <p className="text-xs text-muted-foreground">3 matters listed across 3 forums</p>
                  </div>
                  <span className="rounded-full bg-secondary px-3 py-1 text-[11px] font-medium text-navy">
                    Wed, 12 June
                  </span>
                </div>
                {DIARY.map((d) => (
                  <div key={d.meta} className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:gap-5">
                    <span className="flex w-24 shrink-0 items-center gap-1.5 text-xs font-semibold text-navy">
                      <Clock className="h-3.5 w-3.5 text-gold-dark" />
                      {d.time}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-navy">{d.title}</span>
                      <span className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
                        <MapPin className="h-3 w-3" />
                        {d.court} · {d.room} · {d.meta}
                      </span>
                    </span>
                    <span className="shrink-0 self-start rounded-full border border-gold/40 bg-gold/10 px-2.5 py-1 text-[11px] font-medium text-gold-dark sm:self-auto">
                      {d.stage}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {tab === "case" && (
              <div data-testid="preview-panel-case" className="px-5 py-5">
                <p className="font-display text-xl text-navy">Malhotra vs. State of Maharashtra &amp; Ors.</p>
                <p className="mt-1 text-xs text-muted-foreground">Writ Petition 4812/2023 · Bombay High Court</p>
                <dl className="mt-5 grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                  {CASE_FIELDS.map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-4 border-b border-border/70 pb-2">
                      <dt className="text-xs text-muted-foreground">{k}</dt>
                      <dd className="text-right text-xs font-medium text-navy">{v}</dd>
                    </div>
                  ))}
                </dl>
                <div className="mt-5 flex flex-wrap gap-3">
                  <span className="inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-xs text-navy">
                    <FileText className="h-3.5 w-3.5 text-gold-dark" /> 8 pleadings on file
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-md bg-secondary px-3 py-2 text-xs text-navy">
                    <Users className="h-3.5 w-3.5 text-gold-dark" /> Client: R. Malhotra
                  </span>
                </div>
              </div>
            )}

            {tab === "portal" && (
              <div data-testid="preview-panel-portal" className="px-5 py-5">
                <p className="font-display text-xl text-navy">What your client sees</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Read-only. Your notes, fees and other clients stay private.
                </p>
                <div className="mt-5 space-y-3">
                  {[
                    ["Matter admitted for hearing", "Court has admitted the petition. Next date 12 June."],
                    ["Affidavit filed", "Rejoinder affidavit filed before the Registry."],
                    ["Next hearing scheduled", "Bombay High Court, Court Room 14, 10:30 AM."],
                  ].map(([t, d]) => (
                    <div key={t} className="rounded-md border border-border bg-[#FAFBFD] px-4 py-3">
                      <p className="text-sm font-medium text-navy">{t}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{d}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Chrome>
        </div>
      </div>
    </section>
  );
};
